"use client";

import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";
import ServicesMenu from "./servicesMegaMenu"; 
import MobileServicesMenu from "./mobileServicesMenu";
import Link from "next/link";
import Image from "next/image";
import Logo from "@/../public/assets/images/logoWithoutTxt.svg";
import { useClientOnly } from "@/components/layout/useHydration";
import { useRouter } from "next/navigation";
import axios from "axios";
import { Service } from "@/app/shared/type";
import { API_BASE_URL, USE_BACKEND } from "@/app/api/variable";
import servicesData from "@/app/data/service.json";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false); // mobile menu open
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const mounted = useClientOnly();
  const servicesRef = useRef<HTMLDivElement>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const router = useRouter();

  // Close services dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setDesktopServicesOpen(false);
      }
    };

    if (desktopServicesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [desktopServicesOpen]);

  // Don't render until mounted to prevent hydration mismatch
  if (!mounted) {
    return (
      <header className="font-poppins bg-black text-white w-full top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link href="/">
            <div className="flex items-center gap-2">
              <Image
                src={Logo}
                alt="Nimbus Node Logo"
                width={60}
                height={40}
              />
            </div>
          </Link>
          <div className="hidden font-poppins md:flex space-x-8 items-center">
            <div className="main-menu-item">Services</div>
            <div className="main-menu-item">About Us</div>
            {/* <div className="main-menu-item">Careers</div> */}
            <div className="main-menu-item">Blogs</div>
            {/* <div className="main-menu-item">Contact Us</div> */}
          </div>
          <div className="hidden md:flex items-center gap-2 ml-4">
            {/* <span className="text-sm">🌐</span> */}
            {/* <div className="bg-transparent text-white text-sm">EN</div> */}
            <Link target="_blank" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition" href="https://outlook.office.com/book/ContactUs@nimbusnode.co/?ismsaljsauthenabled">BOOK AN APPOINTMENT</Link>
          </div>
          <div className="md:hidden">
            <Menu size={28} />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-black text-white w-full top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
        {/* Logo */}
        <Link href="/">
          <div className="flex items-center gap-2">
            <Image
              src={Logo}
              alt="Nimbus Node Logo"
              width={60}
              height={40}
            />
          </div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden font-poppins md:flex space-x-8 items-center">
          {/* Desktop Services Dropdown */}
          <div
            ref={servicesRef}
            className="main-menu-item relative cursor-pointer flex items-center gap-1"
            onClick={() => setDesktopServicesOpen(!desktopServicesOpen)}
          >
            <span className="hover:text-cyan-300 leading-[50px]">Services</span>
            {desktopServicesOpen ? <ChevronUp size={24} className="text-cyan-300 ml-2" /> : <ChevronDown size={24} className="text-cyan-300 ml-2" />}
            {desktopServicesOpen && <ServicesMenu />}
           </div>
           <div className="main-menu-item"><Link href="/about">About Us</Link> </div>
           {/* <div className="main-menu-item"><Link href="/#coming-soon">Careers</Link></div> */}
           <div className="main-menu-item"><Link href="/blogs">Blogs</Link></div>
           {/* <div className="main-menu-item"><Link href="/contact">Contact Us</Link></div> */}
           
        </nav>

    {/* Desktop Language Selector */}
          <div className="hidden md:flex items-center gap-2 ml-4">
            {/* <span className="text-sm">🌐</span> */}
            <Link target="_blank" className="book-cls-header inline-block bg-blue-600 text-white px-3 py-2 rounded-md font-semibold hover:bg-blue-700 transition text-white-[12px]" href="https://outlook.office.com/book/ContactUs@nimbusnode.co/?ismsaljsauthenabled">BOOK AN APPOINTMENT</Link>
            {/* <select 
              value={lang} 
              onChange={(e) => setLang(e.target.value)}
              className="bg-transparent text-white border-none outline-none text-sm cursor-pointer hover:text-cyan-300 transition-colors"
              suppressHydrationWarning
            >
              <option value="EN" className="bg-black text-white">EN</option>
              <option value="FR" className="bg-black text-white">FR</option>
            </select> */}
          </div>



        {/* Hamburger Icon */}
        <button
          className="md:hidden focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

                    {/* Mobile Menu */}
       <div className={`font-poppins md:hidden bg-black border-t border-gray-700  transform transition-all duration-300 ease-in-out ${
           isOpen 
             ? 'opacity-100 translate-y-0 max-h-screen px-4 pb-6 pt-4' 
             : 'opacity-0 -translate-y-4 max-h-0 overflow-hidden p-0'
         }`}
       >
        {/* Services */}
        <div>
          <div
            className="flex items-center justify-between py-2 cursor-pointer"
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
          >
            <span className="text-white">Services</span>
            <span className="text-cyan-300">
              {mobileServicesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {mobileServicesOpen && (
            <MobileServicesMenu onItemClick={() => {
              setIsOpen(false);   
              setMobileServicesOpen(false); 
            }} />
          )}
         </div>
        
       {/* Other Menu Items */}
       <Link
         href="/about/"
         className="block py-2 hover:text-cyan-300 transition-colors"
         onClick={() => {
           setIsOpen(false);
           setMobileServicesOpen(false);
         }}
       >
          About Us
        </Link>
        {/* <Link href="/#coming-soon" className="block py-2 hover:text-cyan-300 transition-colors">
          Careers
        </Link> */}
       <Link
         href="/blogs/"
         className="block py-2 hover:text-cyan-300 transition-colors"
         onClick={() => {
           setIsOpen(false);
           setMobileServicesOpen(false);
         }}
       >
          Blogs
        </Link>
        {/* <Link href="/#coming-soon" className="block py-2 hover:text-cyan-300 transition-colors">
          Contact Us
        </Link> */}
        
        {/* Language Selector */}
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex items-center justify-between">
            {/* <span className="text-white font-medium">Language</span> */}
            {/* <div className="flex items-center gap-2">
              <span className="text-sm">🌐</span>
                <select 
                 value={lang} 
                 onChange={(e) => setLang(e.target.value)}
                 className="bg-transparent text-cyan-300 border-none outline-none font-medium"
                 suppressHydrationWarning
               >
                 <option value="EN" className="bg-black text-white">English</option>
                 <option value="FR" className="bg-black text-white">French</option>
               </select>
            </div> */}
          </div>
        </div>
       </div>
    </header>
  );
}
